import { Injectable } from '@angular/core';
import { distinctUntilChanged, shareReplay, tap } from 'rxjs';

// --- Services ---
import {
	AuthService,
	type ILoginBody,
	type IRefreshTokenBody,
} from '@services/auth.service';

// --- Stores ---
import { AuthStore } from '@stores/auth.store';

// --- Utils ---
import { simpleDiff } from '@utils/simple-diff.util';

// --- Interfaces ---
import type { IUser } from '@interfaces/user';

@Injectable({
	providedIn: 'root',
})
export class AuthFacade {
	readonly authState$ = this.authStore.authState$.pipe(
		distinctUntilChanged((prev, curr) => !simpleDiff(prev, curr))
		// * Não aplicar o shareReplay() aqui, pois haverá problemas de login
	);

	constructor(private authService: AuthService, private authStore: AuthStore) {}

	get authStateStorage() {
		return this.authStore.authStateStorage;
	}

	login({ email, password, rememberMe }: ILoginBody, hostname: string) {
		return this.authService
			.login({ email, password }, hostname)
			.pipe(tap(user => this.authStore.login({ user, rememberMe })));
	}

	guestToken(hostname: string) {
		return this.authService
			.guestToken(hostname)
			.pipe(tap(token => this.authStore.setGuestToken(token.token)));
	}

	integrationTokens() {
		return this.authService.integrationTokens();
	}

	publicApiCredentials() {
		return this.authService.publicApiCredentials();
	}

	logout(id?: IUser['id']) {
		return this.authStore.logout().pipe(
			shareReplay(),
			tap(() => id && this.revokeToken(id))
		);
	}

	continueAsGuest() {
		return this.authStore.continueAsGuest();
	}

	retrievePassword(email: IUser['email']) {
		let hostname = window.location.host;
		return this.authService.retrievePassword(email, hostname);
	}

	refreshToken(body: IRefreshTokenBody) {
		return this.authService
			.refreshToken(body)
			.pipe(tap(user => this.authStore.login({ user })));
	}

	revokeToken(id: IUser['id']) {
		return this.authService.revokeToken(id);
	}
}
